import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStarters, type StarterPlan } from '../../services/plansApi';
import { useUserStore } from '../../stores';
import { trackStarterOpened, trackUpgradeCtaClicked } from '../../hooks/useAnalytics';
import './FirstScreen.css';

/** At most three starters are drawn, whatever the server sends. */
export const MAX_STARTERS = 3;

/**
 * `From Ana Silva · ` — or nothing when the plan has no creator to name. The
 * separator is there only when a count follows it (`showCount`).
 */
export const starterFrom = (provenance: string, showCount = true): string => {
  const who = (provenance || '').trim();
  if (!who) return '';
  return showCount ? `From ${who} · ` : `From ${who}`;
};

/**
 * Whether a row shows its count. `times_used` counts public copies, decided
 * once on the server (auto-starters decision 2), and a zero says nothing a
 * visitor should read as social proof, so it renders nothing (decision 8).
 */
export const showsCount = (timesUsed: number): boolean => timesUsed > 0;

interface FirstScreenProps {
  /** Opens the plan chat: the question is the product's one question. */
  onAsk: () => void;
}

/** The anonymous build's plan, waiting for signup (`usePlanChat` writes it). */
const hasPendingPlan = (): boolean => {
  try { return localStorage.getItem('vizday_pending_plan') != null; } catch { return false; }
};

/**
 * The anonymous first screen (Almanac decision 12, the first-open design):
 * the question, the compose line that opens the chat, and up to three
 * starter plans from `GET /api/v1/plans/starters/`, which the server chooses
 * and rotates daily (`docs/plans/AUTO_STARTERS_PLAN.md`). A zero count is
 * hidden, and the where-line goes too when there is also no creator to name.
 * A tap records `starter_opened` (slug and 1-based rank) before it navigates.
 * With no starters — an empty pool or a failed fetch — the question stands
 * alone and no error is shown: the first screen is not the place to report
 * an outage.
 *
 * While an anonymous plan waits to be saved, one line under the question says
 * so and links to the same signup-as-save the chat's save bar starts, so the
 * plan stays reachable after the chat that built it is gone. Never shown to a
 * signed-in user.
 */
const FirstScreen: React.FC<FirstScreenProps> = ({ onAsk }) => {
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const showUnsaved = !user && hasPendingPlan();
  const [starters, setStarters] = useState<StarterPlan[]>([]);

  useEffect(() => {
    let cancelled = false;
    getStarters()
      .then((rows) => {
        if (!cancelled) setStarters(Array.isArray(rows) ? rows.slice(0, MAX_STARTERS) : []);
      })
      .catch(() => { if (!cancelled) setStarters([]); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="first-screen">
      <h1 className="first-ask">Where to, or what kind of day?</h1>
      {showUnsaved && (
        <p className="first-unsaved" data-testid="first-unsaved">
          You have an unsaved plan.{' '}
          <button
            type="button"
            className="first-unsaved-link"
            onClick={() => {
              trackUpgradeCtaClicked('first_screen_save');
              navigate('/signup');
            }}
          >
            Save to my plans
          </button>
        </p>
      )}
      <button type="button" className="first-compose" onClick={onAsk} aria-label="Ask for a plan">
        <span className="first-compose-in">A weekend, a trip, a Saturday…</span>
        <span className="first-compose-send" aria-hidden="true">↑</span>
      </button>
      {starters.length > 0 && (
        <div className="first-starters">
          <div className="first-starters-h">Or start from one of these</div>
          {starters.map((plan, index) => {
            const showCount = showsCount(plan.times_used);
            const from = starterFrom(plan.provenance, showCount);
            return (
              <button
                type="button"
                key={plan.slug}
                className="starter"
                data-testid="starter-row"
                onClick={() => {
                  trackStarterOpened(plan.slug, index + 1);
                  // The public plan route, the same one an Explore plan card opens.
                  navigate(`/plans/p/${plan.slug}`);
                }}
              >
                <span className="starter-what">{plan.title}</span>
                <span className="starter-chev" aria-hidden="true">›</span>
                {(from || showCount) && (
                  <span className="starter-where" data-testid="starter-where">
                    {from}
                    {showCount && (
                      <span className="starter-count" data-testid="starter-count">used {plan.times_used}×</span>
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      <p className="first-note">No account needed to make a plan. You’ll only be asked if you want to keep it.</p>
    </div>
  );
};

export default FirstScreen;
